import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { View, Pressable } from 'react-native';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import { Text } from 'app/src/styles';

import { FIELDS, LINE_FIELDS, EMPTY_LINE } from './new';
import { ESTIMATE_SALES_TAX } from './queries';
import { useTaxLineMirror } from './taxLineMirror';
import { UPDATE_TXN } from '../Txns/queries';

import {
  specialAccount,
  prepareTxnInput,
  creditLessDebitTxnLine,
} from '../Txns/utils';

const EditInvoice = ({ txn, setShowModal, refetch }) => {
  const { accounts, paymentTerms } = useContext(AuthContext);

  const [nestedResources, setNestedResources] = useState(
    txn.txnLines
      .filter((txnLine, index) => index > 0)
      .map(creditLessDebitTxnLine)
  );
  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  // The applySalesTax toggle reflects whether a SALES TAX PD line exists.
  // It's a form-only UI helper; the backend trusts the line list, not the
  // flag.
  const initialApplySalesTax = txn.txnLines.some(
    line => 'sales_tax' === line.account?.special
  );

  const {
    register, unregister, control, handleSubmit, setValue, getValues, watch,
    formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: {
        ...txn,
        balanceAccountId: txn.txnLines[0].account.id,
        applySalesTax: initialApplySalesTax,
      },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [updateTxn] = useMutation(UPDATE_TXN, {
    onCompleted: data => {
      if (data.updateTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.updateTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = 'Invoice';
    input.balanceAccountId =
      specialAccount({ accounts, special: 'ar' }).value;

    const variables = prepareTxnInput(input, FIELDS);
    delete variables.applySalesTax;

    // The mirror hook can drop the SALES TAX PD line from form state, but
    // Rails nested_attributes only destroys persisted children when their
    // id arrives with _destroy: true. If the original tax line's id is no
    // longer in the submitted list, queue it for destruction so the
    // backend can rebalance.
    const persistedTaxLineId = txn.txnLines.find(
      line => 'sales_tax' === line.account?.special
    )?.id;
    if (persistedTaxLineId) {
      const persistedStillInForm = (variables.txnLinesAttributes || []).some(
        line => String(line.id) === String(persistedTaxLineId)
      );
      if (!persistedStillInForm) {
        variables.txnLinesAttributes = [
          ...(variables.txnLinesAttributes || []),
          { id: persistedTaxLineId, _destroy: true },
        ];
      }
    }

    updateTxn({ variables });
  });

  const watchAll = watch();

  const applySalesTaxValue = watchAll?.applySalesTax ?? initialApplySalesTax;

  // Before any nested input registers, watchAll.txnLinesAttributes is empty —
  // fall back to nestedResources so the preview matches the persisted state.
  const formLines =
    watchAll?.txnLinesAttributes?.length ?
      watchAll.txnLinesAttributes :
      nestedResources.map(line => ({
        accountId: line.account?.id,
        creditLessDebit: line.creditLessDebit,
      }));

  const saleId = txn.saleId;

  const [getEstimateSalesTax, { data: estimateData, loading: estimateLoading }] =
    useLazyQuery(ESTIMATE_SALES_TAX);

  useEffect(() => {
    if (!saleId) { return; }
    const handle = setTimeout(() => {
      getEstimateSalesTax({
        variables: {
          saleId,
          lines: formLines.map((line, i) => {
            const original = nestedResources?.[i];
            return {
              accountId: line.accountId || line.account?.id,
              creditLessDebit: parseFloat(line?.creditLessDebit) || 0,
              feeId: line?.feeId || original?.feeId || original?.fee?.id,
            };
          }),
        },
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [saleId, JSON.stringify(formLines)]);

  const addLine = () => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;
    const lastLine = txnLinesWatch ? txnLinesWatch[txnLinesWatch.length - 1] :
      txn.txnLines[txn.txnLines.length - 1];
    const prefix = `txnLinesAttributes.${nestedResources.length}`;

    register(`${prefix}.accountId`, { value: '' });
    register(`${prefix}.creditLessDebit`, { value: '' });
    register(`${prefix}.memo`, { value: '' });
    register(`${prefix}.assetId`, {
      value: lastLine?.assetId || lastLine?.asset?.id
    });
    register(`${prefix}.saleAssetId`, {
      value: lastLine?.saleAssetId || lastLine?.saleAsset?.id
    });
    setNestedResources([...nestedResources, EMPTY_LINE]);
  }

  const taxAccountId =
    specialAccount({ accounts, special: 'sales_tax' })?.value;

  const persistedTaxLine = nestedResources.find(line =>
    (line.account?.id || line.accountId) === taxAccountId
  );
  const persistedTaxValue =
    parseFloat(persistedTaxLine?.creditLessDebit || 0);

  // Treat the persisted tax value as a valid initial estimate so the mirror
  // hook can re-add the line if the user toggles off and back on without
  // waiting for a network round trip.
  const lastEstimate = estimateData?.estimateSalesTax !== undefined ?
    parseFloat(estimateData.estimateSalesTax) :
    persistedTaxValue;
  const estimateReady = true;

  useTaxLineMirror({
    applySalesTax: applySalesTaxValue,
    estimatedTax: lastEstimate,
    estimateReady,
    taxAccountId,
    nestedResources, setNestedResources,
    emptyLine: EMPTY_LINE,
    register, unregister, getValues, setValue,
  });

  const fields = { ...FIELDS };

  fields.paymentTermId = {
    label: 'Payment Terms',
    select: (paymentTerms || []),
    rules: { required: false },
    blank: 'null',
  };

  const lineFields = { ...LINE_FIELDS };

  lineFields.assetId = {
    ...lineFields.assetId,
    txn: txn,
    onFeeCreated: data => {
      refetch();
      setShowModal(false);
    }
  }

  return (
    <View>
      <FormProvider watch={watch}>
        <Fields
          fields={fields}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <NestedFields
          prefix="txnLinesAttributes"
          resources={nestedResources}
          fields={lineFields}
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        {applySalesTaxValue && (estimateLoading || !estimateReady) && (
          <Text style={{ fontSize: 13, fontStyle: 'italic', marginTop: 4 }}>
            SALES TAX PD: Estimating…
          </Text>
        )}

        <Pressable onPress={addLine}>
          <Text style={{ fontSize: 14 }}>+ Add Line</Text>
        </Pressable>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button label="Update Invoice" onPress={handleSubmit(onSubmit)} />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default EditInvoice;
